import Vue from 'vue'
import Vuex, {ActionContext} from 'vuex'
import axios from "axios";
import gorevYoneticisi from "@/plugins/uyap-plugin/store/modules/GorevYoneticisi";
import {AppState, AppStatus, ITenant, ManifestResponse, UyapStatusMessage} from '@/types';
import VueSocketIOExt from 'vue-socket.io-extended';
import {io} from 'socket.io-client';
import app from "@/main";
import {KullaniciBilgileriCevap} from "@/plugins/uyap-plugin/uyap/KullaniciBilgileri";
import {promptForMobilImzaCredentials} from "@/plugins/uyap-plugin/utils/mobil-imza-credentials-modal";
import _Vue from "vue";
import {oturumKimligiUyusuyor} from "@/store/oturumKimligi";
import {isletimSistemiBildirimi, uyariSesi} from "@/plugins/kritikUyari";
import {
    kotaUyarisiKancasiniAyarla,
    devreKesiciKancasiniAyarla,
    durumMesajiKancasiniAyarla,
    DurumMesajiTipi
} from "@/plugins/uyap-plugin/BaglantiKurtarma";
import {PlayerDurum} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";

const socket = io('https://imza.eicrapro.com:3000');
Vue.use(VueSocketIOExt, socket);
Vue.use(Vuex);

// connectToUyap için in-flight kilidi: eşzamanlı çağrılar (Bağlan butonu, socket
// eventi, health check) aynı denemeyi paylaşır; geç biten eski bir deneme yeni
// kurulmuş bağlantıyı false'a ezemez.
let connectToUyapPromise: Promise<void> | null = null;

// Ardışık başarısızlıkta tik atlayarak geri çekilme için.
let healthTikSayaci = 0;

export const HEALTH_CHECK_ARALIK_MS = 30000;
// Health check aralığından büyük olmalı; küçükse paylaşım çalışmaz.
export const IMZA_PROBE_TAZELIK_MS = HEALTH_CHECK_ARALIK_MS + 5000;

export const store = new Vuex.Store<AppState>({
    modules: {
        // @ts-ignore
        gorevYoneticisi
    },
    state: {
        uyapConnection: false,
        appStatus: AppStatus.loading,
        tenant: undefined,
        manifestResponse: {files: []},
        availableCardList: [],
        selectedCardSerial: null,
        tokenExis: false,
        devMode: true,
        eicraproDevMode: false,  // Mock data kullanmak için true yap
        uyapAvukatBilgileri: undefined,
        uyapAvukatBakiye: undefined,
        uyapSorguBakiye: undefined,
        uyapStatusMessage: null,
        connectionHealthCheckInterval: null as number | null,
        healthCheckRetryCount: 0,
        healthCheckMaxRetries: 3,
        // Health check ile durum kartının paylaştığı son yoklama sonucu.
        sonImzaProbe: null as { basarili: boolean, zaman: number } | null,
        // UYAP 5 oturum sınırına takıldığında gösterilen uyarı (null = kapalı).
        oturumLimitiUyarisi: null as { zaman: number } | null,
        // 401 devre kesici açıldığında gösterilen uyarı (null = kapalı).
        devreKesiciUyarisi: null as { zaman: number } | null
    },
    getters: {
        appStatus: (state) => state.appStatus,
        isLoading: (state) => state.appStatus === AppStatus.loading,
        isUpdate: (state) => state.appStatus === AppStatus.update,
        isBrowser: (state) => state.appStatus === AppStatus.browser,
        isElectron: (state) => state.appStatus === AppStatus.electron,
        isUyapConnected: (state) => state.uyapConnection,
        isTokenExis: (state) => state.tokenExis,
        isDevMode: (state) => state.devMode,
        isEicraproDevMode: (state) => state.eicraproDevMode,
        uyapAvukatBilgileri: (state) => state.uyapAvukatBilgileri,
        uyapAvukatBakiye: (state) => state.uyapAvukatBakiye,
        uyapSorguBakiye: (state) => state.uyapSorguBakiye,
        availableCardList: (state) => state.availableCardList,
        selectedCardSerial: (state) => state.selectedCardSerial,
        tenantBilgileri: (state) => state.tenant,
        uyapStatusMessage: (state) => state.uyapStatusMessage,
        healthCheckRetryCount: (state) => state.healthCheckRetryCount,
        isHealthChecking: (state) => state.connectionHealthCheckInterval !== null,
        sonImzaProbe: (state) => state.sonImzaProbe,
        oturumLimitiUyarisi: (state) => state.oturumLimitiUyarisi,
        devreKesiciUyarisi: (state) => state.devreKesiciUyarisi
    },
    mutations: {
        setAppState(state: AppState, value: AppStatus): void {
            state.appStatus = value;
        },
        setManifestResponse(state: AppState, response: ManifestResponse) {
            state.manifestResponse = response;
        },
        setAvailableCardList(state: AppState, value: any) {
            state.availableCardList = value;
        },
        setSelectedCardSerial(state: AppState, serial: string | null) {
            state.selectedCardSerial = serial;
        },
        setUyapConnection(state: AppState, payload: boolean) {
            app.$socket.client.emit("uyapconnection", {
                connected: payload,
                JSESSIONID: payload ? sessionStorage.getItem('JSESSIONID') : ''
            });
            state.uyapConnection = payload;
        },
        setUyapAvukatBilgileri(state: AppState, avukat: KullaniciBilgileriCevap | undefined) {
            state.uyapAvukatBilgileri = avukat;
        },
        setUyapAvukatBakiye(state: AppState, payload: string | undefined) {
            state.uyapAvukatBakiye = payload;
        },
        setUyapSorguBakiye(state: AppState, payload: string | undefined) {
            state.uyapSorguBakiye = payload;
        },
        setTenant(state: AppState, tenant: ITenant) {
            state.tenant = tenant;
        },
        setUyapStatusMessage(state: AppState, payload: UyapStatusMessage | null) {
            state.uyapStatusMessage = payload;
        },
        setHealthCheckInterval(state: AppState, interval: any) {
            state.connectionHealthCheckInterval = interval;
        },
        setHealthCheckRetryCount(state: AppState, count: number) {
            state.healthCheckRetryCount = count;
        },
        setSonImzaProbe(state: AppState, payload: { basarili: boolean, zaman: number }) {
            state.sonImzaProbe = payload;
        },
        setOturumLimitiUyarisi(state: AppState, payload: { zaman: number } | null) {
            state.oturumLimitiUyarisi = payload;
        },
        setDevreKesiciUyarisi(state: AppState, payload: { zaman: number } | null) {
            state.devreKesiciUyarisi = payload;
        },
    },
    actions: {
        /**
         * UYAP'a bağlan.
         *
         * @param payload.force  true ise mevcut oturum geçerli olsa bile yeniden giriş
         *                       yapılır. Kullanıcının açık talebi (Bağlan düğmesi) ve
         *                       kart değişimi için gerekli — aksi halde eski kartın
         *                       oturumu geçerli bulunup yanlış avukatla devam edilir.
         */
        async connectToUyap(ctx: ActionContext<AppState, AppState>, payload?: { force?: boolean }) {
            if (connectToUyapPromise) {
                return connectToUyapPromise;
            }
            connectToUyapPromise = (async () => {
                try{
                    // Her betaLogin UYAP'ta YENİ oturum açar ama eskisini kapatmaz
                    // (clearCookies yalnızca yerel cookie'yi siler, UYAP'ta logout ucu yok).
                    // 5 oturum kotası dolunca PRTL_GNL_100026-1 gelir. Bu yüzden oturum
                    // hâlâ geçerliyse giriş yapılmaz. KullaniciBilgileri önbelleklenmiyor
                    // (cache active:false), yani bu gerçek bir oturum testidir.
                    if (!payload?.force && await ctx.dispatch('uyapOturumuGecerliMi')) {
                        ctx.commit('setUyapConnection', true);
                        await ctx.dispatch('loadUyapMetaData').catch((e) => console.error('UYAP meta verisi yüklenemedi:', e));
                        return;
                    }
                    // Mobil imza ile giriş yapan kullanıcılar için ayrı akış (develop'tan).
                    const loginType = window.localStorage.getItem("loginType");
                    if (loginType == "mobil-imza")
                        await ctx.dispatch("mobilImzaLogin");
                    else
                        await app.$imza.betaLogin();
                    await ctx.dispatch('loadUyapMetaData');
                    ctx.commit('setUyapConnection', true);
                    ctx.commit('setUyapStatusMessage', {
                        type: 'success',
                        text: 'Uyap bağlantısı tamamlandı.',
                        timestamp: Date.now()
                    });
                    //app.$toast.success('Uyap bağlantısı tamamlandı.');
                }catch (error) {
                    ctx.commit('setUyapConnection', false);
                    const errMsg = (error as any)?.message ?? String(error);
                    const message = `Uyap bağlantısı yapılamadı. (${errMsg})`;
                    ctx.commit('setUyapStatusMessage', {
                        type: 'error',
                        text: message,
                        timestamp: Date.now()
                    });
                    throw error instanceof Error ? error : new Error(errMsg);
                } finally {
                    connectToUyapPromise = null;
                }
            })();
            return connectToUyapPromise;
        },
        /**
         * UYAP 5 oturum sınırı uyarısı. Dialog + işletim sistemi bildirimi + ses.
         * Ses yalnızca burada çalar; her uyarıda çalarsa sinyal değerini yitirir.
         */
        oturumLimitiUyarisiGoster(ctx: ActionContext<AppState, AppState>) {
            // Uyarı zaten açıksa tekrar bildirim/ses üretme.
            if (ctx.state.oturumLimitiUyarisi) {
                return;
            }
            ctx.commit('setOturumLimitiUyarisi', {zaman: Date.now()});
            ctx.commit('setUyapStatusMessage', {
                type: 'error',
                text: 'UYAP oturum sınırına ulaşıldı. Açık UYAP oturumlarınızı kapatıp yeniden bağlanın.',
                timestamp: Date.now()
            });
            isletimSistemiBildirimi(
                'UYAP oturum sınırına ulaşıldı',
                'Açık UYAP oturumlarınızı kapatıp yeniden bağlanın.'
            );
            uyariSesi();
        },
        oturumLimitiUyarisiniKapat(ctx: ActionContext<AppState, AppState>) {
            ctx.commit('setOturumLimitiUyarisi', null);
        },
        /**
         * 401 devre kesici açıldı: kısa sürede çok sayıda oturum hatası alındı ve
         * denemeye devam etmek ban riski taşıyor. Bot durdurulur, kullanıcıya
         * dialog + bildirim + ses verilir. Kesici yalnızca kullanıcı bilinçli
         * olarak yeniden başlattığında sıfırlanır.
         */
        devreKesiciUyarisiGoster(ctx: ActionContext<AppState, AppState>) {
            if (ctx.state.devreKesiciUyarisi) {
                return;
            }
            ctx.commit('setPlayerDurum', PlayerDurum.dur);
            ctx.commit('setDevreKesiciUyarisi', {zaman: Date.now()});
            ctx.commit('setUyapStatusMessage', {
                type: 'error',
                text: 'UYAP bağlantısında tekrarlayan hata; ban riskine karşı işlemler durduruldu.',
                timestamp: Date.now()
            });
            isletimSistemiBildirimi(
                'İşlemler durduruldu',
                'UYAP bağlantısında tekrarlayan hata; ban riskine karşı işlemler durduruldu.'
            );
            uyariSesi();
        },
        devreKesiciUyarisiniKapat(ctx: ActionContext<AppState, AppState>) {
            ctx.commit('setDevreKesiciUyarisi', null);
        },
        /** Oturum gerçekten ayakta mı. KullaniciBilgileri önbelleksiz olduğu için canlı testtir. */
        async uyapOturumuGecerliMi(ctx: ActionContext<AppState, AppState>): Promise<boolean> {
            let avukat: KullaniciBilgileriCevap;
            try {
                avukat = await app.$uyap.KullaniciBilgileri().run({});
            } catch (e) {
                return false;
            }

            // Oturum "geçerli" olsa bile YANLIŞ KİMLİĞE ait olabilir.
            // Hesap TC'si sessionStorage'a Electron proxy tarafından yazılır
            // (setConfig; accessToken ile aynı istekte, yani aynı hesaptan).
            const secili = ctx.state.availableCardList?.find(
                (c: any) => c?.certificate?.certificateId === ctx.state.selectedCardSerial
            );
            const denetim = oturumKimligiUyusuyor({
                oturum: avukat?.tcKimlikNo,
                hesap: sessionStorage.getItem('tckn'),
                kart: secili?.certificate?.subjectSerial
            });
            if (!denetim.uyusuyor) {
                console.warn(`Oturum farklı bir ${denetim.sebep === 'hesap' ? 'hesaba' : 'karta'} ait, yeniden giriş yapılacak.`);
                return false;
            }

            ctx.commit('setUyapAvukatBilgileri', avukat);
            return true;
        },

        async setEdevletCookiesToImza(): Promise<void>{
            let cookiesStr = window.sessionStorage.getItem("edevlet-cookies");
            if(cookiesStr){
                const cookies = cookiesStr
                    .split(';')
                    .map(c => c.trim())
                    .filter(Boolean);
                console.log("cookies array", cookies);
                await axios.post(
                    "https://imza.eicrapro.com:6081/cookie",
                    { cookies: cookies },
                    { withCredentials: true }
                );
            }
        },

       async mobilImzaLogin(ctx: ActionContext<AppState, AppState>): Promise<void> {
            try{
                await ctx.dispatch("setEdevletCookiesToImza");
                //Oturum varsa tekrar login olmasina gerek yok...
                let kullaniciBilgileri = await app.$uyap.KullaniciBilgileri().run({});
                console.log("mobilImzaLogin kullaniciBilgileri geldi !!!",kullaniciBilgileri);
                return ;
            }catch (e) {
                console.log("ERROR mobilImzaLogin",e);
                //console.log("Oturum YOK TEKRAR OTURUM AÇMA İSTEĞİYLE DEVAM EDİLECEK.");
                _Vue.prototype.$socket.client.emit("imzaMessage",{
                    title: "Mobil imza oturumu bulunamadı ! Lütfen çıkış yapıp tekrar giriş yapınız.",
                    text:  e.message,
                    icon: "error",
                    logout: true,
                    allowOutsideClick: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Tamam'
                });

                throw new Error("Mobil Imza Oturumu Bulunamadı !");
            }
            //let credentials = window.localStorage.getItem("credentials");
            //if(credentials){
            //    credentials = JSON.parse(credentials);
            //}else {
             //   const formData = await promptForMobilImzaCredentials();
             //   if (!formData) {
             //       throw new Error('Mobil imza girişi iptal edildi.');
              //  }
              //  credentials = formData;
              //  window.localStorage.setItem("credentials", credentials);
            //}
            //await app.$imza.eDevletMobilImzaLogin(credentials);
            //await ctx.dispatch("setEdevletCookiesToImza");
        },

        async loadUyapMetaData(ctx: ActionContext<AppState, AppState>): Promise<void> {
            const [bakiyeResult, sorguBakiyeResult, kullaniciResult] = await Promise.allSettled([
                app.$uyap.AvukatBakiyeSorgula().run({}),
                app.$uyap.SorguBakiyesi().run({}),
                app.$uyap.KullaniciBilgileri().run({})
            ]);

            if (bakiyeResult.status === "fulfilled") {
                ctx.commit('setUyapAvukatBakiye', (bakiyeResult.value as { value: string }).value);
            } else {
                ctx.commit('setUyapAvukatBakiye', undefined);
                console.error('AvukatBakiyeSorgula failed', bakiyeResult.reason);
            }

            if (sorguBakiyeResult.status === "fulfilled") {
                const sorguBakiye = (sorguBakiyeResult.value as { sorguBakiye: number }).sorguBakiye;
                ctx.commit('setUyapSorguBakiye', String(sorguBakiye));
            } else {
                ctx.commit('setUyapSorguBakiye', undefined);
                console.error('SorguBakiyesi failed', sorguBakiyeResult.reason);
            }

            if (kullaniciResult.status === "fulfilled") {
                ctx.commit('setUyapAvukatBilgileri', kullaniciResult.value as KullaniciBilgileriCevap);
            } else {
                ctx.commit('setUyapAvukatBilgileri', undefined);
                console.error('KullaniciBilgileri failed', kullaniciResult.reason);
            }

            app.$socket.client.emit("uyapAvukatBilgileri", {
                uyapAvukatBilgileri: ctx.state.uyapAvukatBilgileri,
                uyapAvukatBakiye: ctx.state.uyapAvukatBakiye,
                uyapSorguBakiye: ctx.state.uyapSorguBakiye
            });

            // KullaniciBilgileri oturumun asıl doğrulamasıdır; alınamadıysa oturum
            // geçersizdir ve çağıranlar (connectToUyap, checkUyapSessionNew) bunu bilmeli.
            // Bakiye hataları ise bağlantıyı düşürmez, sessizce geçilir.
            if (kullaniciResult.status === "rejected") {
                const reason = kullaniciResult.reason;
                throw reason instanceof Error ? reason : new Error(String(reason ?? 'UYAP oturumu doğrulanamadı.'));
            }
        },
        async availableCards(ctx: ActionContext<AppState, AppState>, payload?: { silent?: boolean }) {
            console.log("availableCards triggered");
            const silent = Boolean(payload?.silent);
            try{
                let data = await app.$imza.availableCards();
                //console.log("data availableCards",data);
                ctx.commit('setAvailableCardList', data);
                if (Array.isArray(data) && data.length > 0) {
                    const current = ctx.state.selectedCardSerial;
                    // Yeni yapıda kart kimliği card.certificate.certificateId
                    const match = data.find((card: any) => card?.certificate?.certificateId === current);
                    const fallbackSerial = data[0]?.certificate?.certificateId ?? null;
                    ctx.commit('setSelectedCardSerial', match ? current : fallbackSerial);
                    // 'İ'.toLowerCase() 'i'+U+0307 ürettiği için Türkçe metinlerde
                    // includes eşleşmesi kaçar; tr locale ile küçült.
                    const currentMessage = ctx.state.uyapStatusMessage?.text?.toLocaleLowerCase('tr') ?? '';
                    if (currentMessage.includes('kart') || currentMessage.includes('imzacı') || currentMessage.includes('program')) {
                        ctx.commit('setUyapStatusMessage', null);
                    }
                } else {
                    ctx.commit('setSelectedCardSerial', null);
                }
                if (!silent) {
                    //app.$toast.success('Kart listesi alındı');
                }
            }catch (e) {
                console.log("CATCH E",e);
                const errMsg = e instanceof Error ? e.message : String(e);
                ctx.commit('setAvailableCardList', []);
                ctx.commit('setSelectedCardSerial', null);
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: errMsg,
                    timestamp: Date.now()
                });
                if (!silent) {
                    app.$toast.error(errMsg);
                }
            }
        },
        selectSmartCard(ctx: ActionContext<AppState, AppState>, serial: string | null) {
            ctx.commit('setSelectedCardSerial', serial);
        },
        openUyapWindow() {
            window.open('https://avukatbeta.uyap.gov.tr/', "_blank");
        },
        async fastLoginUyap(ctx: ActionContext<AppState, AppState>) {
            try {
                let avukat = await app.$uyap.KullaniciBilgileri().run({});
                ctx.commit('setUyapConnection', true);
                ctx.commit('setUyapAvukatBilgileri', avukat);
            } catch (e) {
                // Oturum geçersiz: tam e-imza girişine düş.
                await ctx.dispatch('connectToUyap');
                return;
            }
            // Oturum yukarıda doğrulandı; meta verideki geçici bir hata tam
            // e-imza girişine (PIN dialogu) tırmandırılmamalı.
            await ctx.dispatch('loadUyapMetaData').catch((e) => console.error('UYAP meta verisi yüklenemedi:', e));
        },
        async checkUyapSessionNew(ctx: ActionContext<AppState, AppState>) {
            console.log("checkUyapSessionNew triggered");
            try {
                // updateToken ile tetiklenir, yani HESAP DEĞİŞMİŞ olabilir: setConfig
                // sessionStorage'a yeni accessToken ile birlikte yeni tckn de yazar.
                // Oturumun yeni hesaba ait olduğu doğrulanmadan bağlı sayılmamalı,
                // aksi halde bir avukatın UYAP oturumuyla başkasının görevleri işlenir.
                if (!await ctx.dispatch('uyapOturumuGecerliMi')) {
                    ctx.commit('setUyapConnection', false);
                    return;
                }
                await ctx.dispatch('loadUyapMetaData');
                ctx.commit('setUyapConnection', true);
            } catch (error) {
                ctx.commit('setUyapConnection', false);
            }
        },
        async checkUyapSession(ctx: ActionContext<AppState, AppState>) {
            console.log("checkUyapSession triggered");
            try {
                if (ctx.getters.isEicraproDevMode) {
                    ctx.commit('setUyapConnection', true);
                } else {
                    await app.$imza.setCookieToImza();
                    await ctx.dispatch("fastLoginUyap");
                    ctx.commit('setUyapStatusMessage', {
                        type: 'success',
                        text: 'Uyap oturumu doğrulandı.',
                        timestamp: Date.now()
                    });
                }
            } catch (error) {
                ctx.commit('setUyapConnection', false);
                const message = `Uyap oturumu doğrulanamadı. ${String(error ?? '')}`.trim();
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: message,
                    timestamp: Date.now()
                });
            }
        },
        async setTasksSocket(ctx: ActionContext<AppState, AppState>) {
            // context.di/getTasksFromServer
            await ctx.dispatch("getTasksFromServer")
            app.$socket.client.emit("tasks", ctx.getters.getGorevler);
        },
        async load(ctx: ActionContext<AppState, AppState>): Promise<void> {
            ctx.commit("setAppState", AppStatus.loading);
            if (sessionStorage.getItem('accessToken'))
                ctx.state.tokenExis = true;
            if (!window.zirvelib) {
                return ctx.commit('setAppState', AppStatus.browser);
            } else {
                const updateRequires = !await ctx.dispatch('isLocalUpToDate');
                console.log("updateRequires", updateRequires);
                if (updateRequires) {
                    return ctx.commit('setAppState', AppStatus.update);
                }
                try {
                    if (!await ctx.dispatch('checkSignService')) {
                        await ctx.dispatch('startSignService');
                    }
                    if (!await ctx.dispatch('checkProxyService')) {
                        await ctx.dispatch('startProxyService');
                    }
                    return ctx.commit('setAppState', AppStatus.electron);
                } catch (e) {
                    console.log(e);
                    return ctx.commit('setAppState', AppStatus.error);
                }
            }
        },
        async isLocalUpToDate(): Promise<boolean> {
            return new Promise((resolve) => {
                if (!window.zirvelib) {
                    return resolve(false);
                }
                if (!window.zirvelib?.version) {
                    return resolve(false);
                }
                console.log("window.zirvelib.version", window.zirvelib.version);
                return resolve(
                    window.zirvelib.version === "2.0.0"
                );
            });
        },

        async checkSignService(): Promise<boolean> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                const heartbeatPromise = axios.post('http://127.0.0.1:4723/xml/heartbeat', {});
                heartbeatPromise.then(() => {
                    console.log('Sign service is running');
                    resolve(true);
                }).catch((error) => {
                    console.warn('Sign service check failed:', error.message);
                    resolve(false);
                });
            })
        },
        async checkProxyService(): Promise<boolean> {
            return new Promise((resolve) => {
                const heartbeatPromise = axios.post('https://imza.eicrapro.com:6090/xml/heartbeat', {});
                heartbeatPromise.then(() => {
                    console.log('Proxy service is running');
                    resolve(true);
                }).catch((error) => {
                    console.warn('Proxy service check failed:', error.message);
                    resolve(false);
                });
            });
        },
        /** Java imza servisi durumu. Taze sonuç varsa yeni istek atılmaz. */
        async imzaServisiDurumu(
            ctx: ActionContext<AppState, AppState>,
            payload?: { tazelikMs?: number }
        ): Promise<boolean> {
            const tazelikMs = payload?.tazelikMs ?? IMZA_PROBE_TAZELIK_MS;
            const son = ctx.state.sonImzaProbe;
            if (son && (Date.now() - son.zaman) < tazelikMs) {
                return son.basarili;
            }
            const basarili = await ctx.dispatch('checkSignService');
            ctx.commit('setSonImzaProbe', {basarili, zaman: Date.now()});
            return basarili;
        },
        async startSignService(): Promise<void> {
            console.log("Starting Sign Service");
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.signManager.start();
                window.setTimeout(() => {
                    resolve();
                }, 2000)
            })
        },

        async startProxyService(ctx: ActionContext<AppState, AppState>): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.proxyManager.start();
                window.zirvelib.socketManager.start();
                window.setTimeout(() => {
                    resolve();
                }, 2000)
            });
        },

        async stopSignService(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.signManager.kill();
                window.setTimeout(() => {
                    resolve();
                }, 1500)
            })
        },
        async stopProxyService(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.proxyManager.stop();
                window.zirvelib.socketManager.stop();
                resolve();
            })
        },
        /**
         * İmza servisi (6090 -> 4723) canlılık yoklaması.
         *
         * connectToUyap ÇAĞIRMAZ: Java login yolunda değil (login 6075 + 6081
         * üzerinden), ölü olması oturumun geçersiz olduğu anlamına gelmez.
         * Oturum kurtarma UyapRequest ve GorevYoneticisi içinde zaten var.
         */
        async checkHeartbeat(ctx: ActionContext<AppState, AppState>): Promise<boolean> {
            let canli = false;
            try {
                const response = await fetch('https://imza.eicrapro.com:6090/xml/heartbeat', {
                    method: 'POST',
                    credentials: 'include'
                });
                canli = response.ok;
            } catch (error) {
                console.error('İmza servisi yoklaması başarısız:', error);
            }

            ctx.commit('setSonImzaProbe', {basarili: canli, zaman: Date.now()});

            if (canli) {
                ctx.commit('setHealthCheckRetryCount', 0);
                return true;
            }

            const hata = (ctx.state.healthCheckRetryCount || 0) + 1;
            ctx.commit('setHealthCheckRetryCount', hata);

            if (hata === ctx.state.healthCheckMaxRetries) {
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: 'İmza servisine ulaşılamıyor. Doküman imzalama çalışmayabilir; ' +
                          'ana ekrandan servisi yeniden başlatmayı deneyin.',
                    timestamp: Date.now()
                });
                console.error('İmza servisi yoklaması üst üste başarısız');
            }
            return false;
        },
        startHealthCheck(ctx: ActionContext<AppState, AppState>) {
            // Zaten çalışıyorsa tekrar başlatma
            if (ctx.state.connectionHealthCheckInterval !== null) {
                console.log('Health check zaten çalışıyor');
                return;
            }

            console.log('Health check başlatıldı');

            // Ardışık başarısızlıkta geri çekilme: 30 -> 60 -> 120 sn.
            const TABAN_MS = HEALTH_CHECK_ARALIK_MS;
            const TAVAN_MS = 120000;

            const interval = setInterval(async () => {
                const hata = ctx.state.healthCheckRetryCount || 0;

                // Tik sabit olduğu için geri çekilme tik atlayarak uygulanır.
                if (hata > 0) {
                    const beklenenMs = Math.min(TABAN_MS * Math.pow(2, hata), TAVAN_MS);
                    const atlanacakTik = Math.round(beklenenMs / TABAN_MS);
                    healthTikSayaci++;
                    if (healthTikSayaci % atlanacakTik !== 0) {
                        return;
                    }
                }

                // Bağlıyken normal izleme; bağlantı reconnect denemesi sırasında
                // düştüyse retry penceresi boyunca denemeye devam et — aksi halde
                // ilk başarısız denemeden sonra otomatik kurtarma tamamen durur
                // ve max-retry mesajına hiç ulaşılamaz.
                const retrying = hata > 0 && hata < ctx.state.healthCheckMaxRetries;
                if (ctx.getters.isUyapConnected || retrying) {
                    await ctx.dispatch('checkHeartbeat');
                }
            }, TABAN_MS);

            ctx.commit('setHealthCheckInterval', interval);
        },
        stopHealthCheck(ctx: ActionContext<AppState, AppState>) {
            if (ctx.state.connectionHealthCheckInterval !== null) {
                clearInterval(ctx.state.connectionHealthCheckInterval);
                ctx.commit('setHealthCheckInterval', null);
                ctx.commit('setHealthCheckRetryCount', 0);
                healthTikSayaci = 0;
                console.log('Health check durduruldu');
            }
        },
    }
})

// Kota dolduğunda uyarıyı göster. Kanca burada kaydediliyor ki BaglantiKurtarma
// store/DOM'a bağlanmasın ve uyap-plugin'i kullanan diğer uygulamalarda da
// sorunsuz çalışsın.
kotaUyarisiKancasiniAyarla(() => {
    store.dispatch('oturumLimitiUyarisiGoster');
});

// 401 devre kesici açıldığında: bot durur, engelleyici dialog açılır.
devreKesiciKancasiniAyarla(() => {
    store.dispatch('devreKesiciUyarisiGoster');
});

// Kurtarma sırasındaki durum mesajı. Bu uygulamada UyapStatusCard'da satır içi
// gösteriliyor; icrapro-app aynı kancayı toast'a bağlıyor.
durumMesajiKancasiniAyarla((tip: DurumMesajiTipi, metin: string) => {
    store.commit('setUyapStatusMessage', {
        type: tip,
        text: metin,
        timestamp: Date.now()
    });
});
